<script setup lang="ts">
import { AuctionState, ComputedState, DirectSaleComputedState, SellingMethod, ValueAddedServiceCode, type ValueAddedServiceDto } from '~/apiClient';
import { INJECTION_KEYS } from '~/constants/injectionKeys';
import { type SaleItemEntity } from '~/types/sale.type';

type ExtendedVas = ValueAddedServiceDto & { showAsPurchasable: boolean };

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
});

// COMPOSABLES
const { tt, tn } = useTypedI18n();
const { isLoggedIn } = useAuthUtils();
const { goToLoginPage } = useLoginRouteUtils();

// DATA
const isLogisticsModalOpen = ref(false);
const logisticsPrice = ref<number>();
const logisticsFullAddress = ref<string>();

const valueAddedServices = inject(INJECTION_KEYS.VALUE_ADDED_SERVICES);

// Avoid show VAS for sold Sale (see BSD-2193 for further details)
// It can lead to ambiguity between the VAS that are available and those that have actually been purchased.
const showVas = computed(() => {
    if (sale.sellingMethod === SellingMethod.Auction) {
        return [AuctionState.Pending, AuctionState.Running].includes(sale.state) || sale.computedState === ComputedState.UnderNegotiation;
    }

    if (sale.sellingMethod === SellingMethod.DirectSale) {
        return sale.computedState === DirectSaleComputedState.NoActiveOrder;
    }

    return false;
});

const extendedVas = computed<ExtendedVas[]>(() =>
    (valueAddedServices?.value ?? []).filter(vas => vas.enabled).map(vas => ({ ...vas, showAsPurchasable: vas.purchasable || !isLoggedIn.value }))
);

const hasLogistics = computed(
    () => !!extendedVas.value.find(vas => vas.valueAddedServiceCode === ValueAddedServiceCode.LogisticsService && vas.showAsPurchasable)
);

// METHODS
const getLabelClass = (vas: ExtendedVas): string => (!vas.showAsPurchasable ? 'text-neutral-400' : 'text-neutral-700');

const getValueClass = (vas: ExtendedVas): string => (!vas.showAsPurchasable ? 'text-neutral-500 italic' : 'text-primary font-semibold');

const onSelectedAddress = (price?: number, fullAddress?: string): void => {
    logisticsPrice.value = price;
    logisticsFullAddress.value = fullAddress;
};
</script>

<template>
    <div v-if="extendedVas.length && showVas">
        <p class="text-sm font-semibold">{{ tt('vas.services.title') }}</p>

        <div v-for="vas of extendedVas" :key="vas.valueAddedServiceCode" class="text-sm leading-6">
            <template v-if="vas.valueAddedServiceCode === ValueAddedServiceCode.LogisticsService">
                <div class="flex justify-between">
                    <span class="flex items-center gap-1">
                        <span :class="getLabelClass(vas)">
                            <span>{{ tt(`vas.${vas.valueAddedServiceCode}.title`) }}</span>
                            <span v-if="!isLoggedIn" class="ms-0.5">*</span>
                        </span>
                        <BaseTooltip :text="tt(`sdp.vas.tooltip.${vas.valueAddedServiceCode}`)">
                            <UIcon class="size-4" name="lucide:info" />
                        </BaseTooltip>
                    </span>

                    <span v-if="!vas.showAsPurchasable" :class="getValueClass(vas)">{{ tt('sdp.vas.notAvailable') }}</span>
                    <template v-else>
                        <span v-if="logisticsPrice" :class="getValueClass(vas)">{{ tn(logisticsPrice, 'currency') }}</span>
                        <UButton
                            v-else
                            variant="link"
                            size="xs"
                            :label="tt('sdp.vas.logistics.compute')"
                            class="p-0 text-sm"
                            trailing-icon="lucide:square-pen"
                            @click="isLogisticsModalOpen = true"
                        />
                    </template>
                </div>

                <div v-if="logisticsFullAddress" class="flex justify-between gap-6">
                    <span class="text-primary truncate text-xs leading-6 italic">{{ logisticsFullAddress }}</span>

                    <UButton
                        variant="link"
                        size="xs"
                        :label="tt('sdp.vas.logistics.update')"
                        class="p-0 text-sm"
                        trailing-icon="lucide:square-pen"
                        @click="isLogisticsModalOpen = true"
                    />
                </div>
            </template>

            <template v-else>
                <div class="flex justify-between">
                    <span class="flex items-center gap-1">
                        <span :class="getLabelClass(vas)">
                            <span>{{ tt(`vas.${vas.valueAddedServiceCode}.title`) }}</span>
                            <span v-if="!isLoggedIn" class="ms-0.5">*</span>
                        </span>
                        <BaseTooltip :text="tt(`sdp.vas.tooltip.${vas.valueAddedServiceCode}`)">
                            <UIcon class="size-4" name="lucide:info" />
                        </BaseTooltip>
                    </span>

                    <span v-if="!vas.showAsPurchasable" :class="getValueClass(vas)">{{ tt('sdp.vas.notAvailable') }}</span>
                    <span v-else-if="vas.totalPrice" :class="getValueClass(vas)">{{ tn(vas.totalPrice, 'currency') }}</span>
                </div>
            </template>
        </div>

        <p v-if="!isLoggedIn" class="text-xs">
            <span class="me-0.5">*</span>
            <i18n-t scope="global" tag="span" keypath="sdp.vas.guestDisclaimer">
                <template #loginOrSignup>
                    <UButton class="p-0" size="sm" variant="link" :label="tt('sdp.vas.loginOrSignup')" @click="goToLoginPage" />
                </template>
            </i18n-t>
        </p>
    </div>

    <SdpSalePanelVasLogisticsModal v-if="hasLogistics" v-model:open="isLogisticsModalOpen" :sale="sale" @select-address="onSelectedAddress" />
</template>
